import { Table, TableColumn } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONVERSATION_TYPE_LABEL_KEYS,
  type ConversationInfoResponse,
  getPersonNamesByRole,
} from '@/entities/conversations';
import { PERSON_ROLES } from '@/entities/conversations/model/api/constants';
import { useTimeDisplayFormat } from '@/entities/settings';
import { cn, formatDateTime } from '@/shared/lib';
import { formatTimeInterval, withSecondsLabel } from '@/shared/lib/utils/string';
import { NAME_COLUMN_SIZE, VALUE_MIN_SIZE } from './dialogDataTables';
import styles from './dialogDataTables.module.css';

/** Строка таблицы основных полей */
type FieldRow = {
  id: string;
  name: string;
  value: string;
};

/** Строка таблицы участников */
type ParticipantRow = {
  id: string;
  name: string;
  value: string;
};

/** Форматирует булево значение наличия транскрибации; null/undefined — пустая строка */
const formatHasTranscription = (value: boolean | null | undefined, yesLabel: string, noLabel: string): string => {
  if (value === true) return yesLabel;
  if (value === false) return noLabel;
  return '';
};

type Props = {
  conversation: ConversationInfoResponse;
};

/**
 * Панель «Общее» — отображает поля самой записи диалога (внешний идентификатор, время начала
 * и завершения, длительность, признак транскрибации, вид диалога) и участников. Каждое свойство показано
 * одной строкой: среди значений атрибутов этих свойств нет. Путь к файлу записи не показывается —
 * он нужен интерфейсу лишь для запроса самой записи. Данных атрибутов панель не содержит:
 * они распределены по группам в дереве навигации.
 */
export const MainFieldsPanel: FC<Props> = ({ conversation }) => {
  const { t } = useTranslation(['dialogs']);
  const { t: tCommon } = useTranslation('common');
  const timeDisplayFormat = useTimeDisplayFormat();

  const fieldRows = useMemo<FieldRow[]>(
    () => [
      { id: 'externalId', name: t('dialogs:externalId'), value: conversation.externalId ?? '' },
      { id: 'startedAt', name: t('dialogs:startedAt'), value: formatDateTime(conversation.createdAt) },
      { id: 'finishedAt', name: t('dialogs:finishedAt'), value: formatDateTime(conversation.processedAt) },
      {
        id: 'duration',
        name: withSecondsLabel(t('dialogs:duration'), timeDisplayFormat, tCommon('secondsSuffix')),
        value: conversation.duration == null ? '' : formatTimeInterval(conversation.duration, timeDisplayFormat),
      },
      {
        id: 'hasTranscription',
        name: t('dialogs:hasTranscription'),
        value: formatHasTranscription(
          conversation.hasTranscription,
          t('dialogs:hasTranscriptionYes'),
          t('dialogs:hasTranscriptionNo'),
        ),
      },
      {
        id: 'conversationType',
        name: t('dialogs:conversationType'),
        value: conversation.conversationType ? t(CONVERSATION_TYPE_LABEL_KEYS[conversation.conversationType]) : '',
      },
    ],
    [
      conversation.externalId,
      conversation.createdAt,
      conversation.processedAt,
      conversation.duration,
      conversation.hasTranscription,
      conversation.conversationType,
      timeDisplayFormat,
      t,
      tCommon,
    ],
  );

  const fieldColumns: TableColumn<FieldRow>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: t('dialogs:tableSection.attributeName'),
        size: NAME_COLUMN_SIZE,
        cellClassName: () => 'min-w-0 whitespace-normal wrap-break-word font-semibold',
      },
      {
        accessorKey: 'value',
        header: t('dialogs:tableSection.attributeValue'),
        minSize: VALUE_MIN_SIZE,
        cellClassName: () => cn('min-w-0 whitespace-normal wrap-break-word', styles.valueCell),
      },
    ],
    [t],
  );

  const participantRows = useMemo<ParticipantRow[]>(() => {
    const employees = getPersonNamesByRole(conversation.persons, PERSON_ROLES.EMPLOYEE);
    const clients = getPersonNamesByRole(conversation.persons, PERSON_ROLES.CLIENT);
    const rows: ParticipantRow[] = [];
    if (employees.length > 0) {
      rows.push({ id: 'employees', name: t('dialogs:employees'), value: employees.join('\n') });
    }
    if (clients.length > 0) {
      rows.push({ id: 'clients', name: t('dialogs:clients'), value: clients.join('\n') });
    }
    return rows;
  }, [conversation.persons, t]);

  const participantColumns: TableColumn<ParticipantRow>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: t('dialogs:tableSection.attributeName'),
        size: NAME_COLUMN_SIZE,
        cellClassName: () => 'min-w-0 whitespace-normal wrap-break-word font-semibold',
      },
      {
        accessorKey: 'value',
        header: t('dialogs:tableSection.attributeValue'),
        minSize: VALUE_MIN_SIZE,
        cellClassName: () => cn('min-w-0 whitespace-normal wrap-break-word', styles.valueCell),
        cell: ({ row }) => <span className="whitespace-pre-line">{row.original.value}</span>,
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className={cn(styles.tableScope, 'flex flex-col gap-2')}>
        <h3 className="text-sm font-medium text-muted-foreground">{t('dialogs:dialogDates')}</h3>
        <Table
          type="simple"
          data={fieldRows}
          columns={fieldColumns}
          getKey={(row) => row.id}
          hideHeader
          virtualization={{ enable: false }}
          styles={{ size: 'sm' }}
          className={styles.tableRoot}
          classNames={{ table: cn('w-auto', styles.fieldsTable) }}
        />
      </div>
      {participantRows.length > 0 && (
        <div className={cn(styles.tableScope, 'flex flex-col gap-2')}>
          <h3 className="text-sm font-medium text-muted-foreground">{t('dialogs:dialogParticipants')}</h3>
          <Table
            type="simple"
            data={participantRows}
            columns={participantColumns}
            getKey={(row) => row.id}
            hideHeader
            virtualization={{ enable: false }}
            styles={{ size: 'sm' }}
            className={styles.tableRoot}
            classNames={{ table: cn('w-auto', styles.fieldsTable) }}
          />
        </div>
      )}
    </div>
  );
};
